import { useEffect, useState } from 'react'
import moment from 'moment'
import { useFormState, useForm } from 'react-final-form'

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'

import ArrowForward from '@mui/icons-material/ArrowForward'

import FormRadio from '../../components/FormRadio'
import FormButton from '../../components/FormButton'
import FormError from '../../components/FormError'
import FormField from '../../components/FormField'
import FormWizard from '../wizard'

import { FormSectionProps } from '../../interfaces/form'
import { useTheme } from '@emotion/react'

const GetNextDates = require('get-next-dates')

const radioStyle = {
  paddingBottom: 0,
  display: 'none',
}

const radioOverrideMuiStyle = {
  '&.MuiRadio-root': {
    '&.Mui-checked': {
      '+div, +span.timeOfDay, +span.time': {
        '.MuiTypography-root': {
          backgroundColor: `primary.main`,
          color: '#FFF !important',
        },
        backgroundColor: `primary.main`,
        color: '#FFF !important',
      },
    },
  },
}

const textStyle = {
  padding: '16px 0px',
  borderRadius: '8px',
  display: 'inline-block',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'primary.main',
  marginTop: 0,
  marginRight: '6px',
  marginBottom: '10px',
  fontWeight: 'bold',
  verticalAlign: 'top',
}

const textHoverStyle = {
  minWidth: '58px !important',
  '&:hover': {
    backgroundColor: 'primary.main',
    color: '#fff',
    '.MuiTypography-root': {
      backgroundColor: 'primary.main',
      color: '#fff',
    },
  },
}

const dateListItemStyle = {
  minWidth: 86,
  minHeight: 95,
}

const timeOfDayStyle = {
  width: '100%',
}

const BookAppointment = ({ validate }: FormSectionProps) => {
  const form = useForm()
  const formState = useFormState()
  const [dateList, setDateList] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  //@ts-ignore
  const custom = theme?.custom;

  const getDates = (date: string) => {
    const dates = GetNextDates(7, new Date(date)).filter(
      (date: string) => moment(date).day() > 0 && moment(date).day() < 6
    )

    setDateList([...dateList, ...dates])
  }

  const renderDateList = () => {
    return (
      <div id="date-list-section" style={{ marginBottom: 20 }}>
        {dateList.map((date) => (
          <label
            htmlFor={moment(date).format('DD/MM/YYYY')}
            style={{ textAlign: 'center' }}
            key={moment(date).format('DD/MM/YYYY')}
          >
            <FormRadio
              id={moment(date).format('DD/MM/YYYY')}
              name="appointmentDate"
              value={moment(date).format('YYYY-MM-DD')}
              style={radioStyle}
              overrideMuiStyle={radioOverrideMuiStyle}
            />
            <Typography
              component="div"
              style={{ ...dateListItemStyle }}
              sx={{ ...textStyle, ...textHoverStyle }}
            >
              <Typography fontSize={14} color="#595959">
                {moment(date).format('ddd')}
              </Typography>
              <Typography fontSize={20} color="#595959">
                {moment(date).date()}
              </Typography>
              <Typography fontSize={14} color="#595959">
                {moment(date).format('MMM')}
              </Typography>
            </Typography>
          </label>
        ))}
        {dateList.length && (
          <FormButton
            type="button"
            style={{ ...dateListItemStyle, minHeight: '106px' }}
            sx={{ ...textStyle, ...textHoverStyle }}
            onClick={() => getDates(dateList[dateList.length - 1])}
            disableRipple
          >
            <ArrowForward />
          </FormButton>
        )}
      </div>
    )
  }

  const renderTimeOfDay = () => {
    const timeOfDayList = ['Morning', 'Afternoon', 'Evening']

    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        {timeOfDayList.map((timeOfDay) => (
          <Grid item xs={12} md={4} key={timeOfDay}>
            <label htmlFor={timeOfDay} style={{ textAlign: 'center' }}>
              <FormRadio
                id={timeOfDay}
                name="appointmentTimeOfDay"
                value={timeOfDay}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="timeOfDay"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                {timeOfDay}
              </Typography>
            </label>
          </Grid>
        ))}
      </Grid>
    )
  }

  const renderTime = () => {
    const { values } = formState
    const time: Record<any, string[]> = {
      morning: ['09:00', '10:00', '11:00'],
      afternoon: ['12:00', '13:00', '14:00', '15:00'],
      evening: ['16:00', '17:00'],
    }

    if (!values?.appointmentTimeOfDay) return null

    return (
      <Grid container spacing={2} sx={{ marginBottom: '40px' }}>
        {time[values?.appointmentTimeOfDay.toLowerCase()].map((hour) => (
          <Grid item xs={12} md={3} key={hour}>
            <label htmlFor={hour} style={{ textAlign: 'center' }}>
              <FormRadio
                id={hour}
                name="appointmentTime"
                value={hour}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                {hour}
              </Typography>
            </label>
          </Grid>
        ))}
      </Grid>
    )
  }

  const renderTelephoneCall = () => {
    return (
      <div id="telephone-call-section" style={{ marginBottom: 20 }}>
        <Typography fontSize={14} color="#595959" style={{ marginBottom: 20 }}>
          Telephone Call
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <label htmlFor="isTelephoneCallYes" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isTelephoneCallYes"
                name="isTelephoneCall"
                value={'yes'}
                onClick={() => {
                  form.change('isVideoCall', 'no')
                  form.change('isMeeting', 'no')
                }}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                Yes
              </Typography>
            </label>
          </Grid>
          <Grid item xs={12} md={3}>
            <label htmlFor="isTelephoneCallNo" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isTelephoneCallNo"
                name="isTelephoneCall"
                value={'no'}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                No
              </Typography>
            </label>
          </Grid>
        </Grid>
      </div>
    )
  }

  const renderVideoCall = () => {
    return (
      <div id="video-call-section" style={{ marginBottom: 20 }}>
        <Typography fontSize={14} color="#595959" style={{ marginBottom: 20 }}>
          Video Call
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <label htmlFor="isVideoCallYes" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isVideoCallYes"
                name="isVideoCall"
                value={'yes'}
                onClick={() => {
                  form.change('isTelephoneCall', 'no')
                  form.change('isMeeting', 'no')
                }}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                Yes
              </Typography>
            </label>
          </Grid>
          <Grid item xs={12} md={3}>
            <label htmlFor="isVideoCallNo" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isVideoCallNo"
                name="isVideoCall"
                value={'no'}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                No
              </Typography>
            </label>
          </Grid>
        </Grid>
      </div>
    )
  }

  const renderMeeting = () => {
    return (
      <div id="meeting-section" style={{ marginBottom: 20 }}>
        <Typography fontSize={14} color="#595959" style={{ marginBottom: 20 }}>
          Face to Face
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <label htmlFor="isMeetingYes" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isMeetingYes"
                name="isMeeting"
                value={'yes'}
                onClick={() => {
                  form.change('isTelephoneCall', 'no')
                  form.change('isVideoCall', 'no')
                }}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                Yes
              </Typography>
            </label>
          </Grid>
          <Grid item xs={12} md={3}>
            <label htmlFor="isMeetingNo" style={{ textAlign: 'center' }}>
              <FormRadio
                id="isMeetingNo"
                name="isMeeting"
                value={'no'}
                style={radioStyle}
                overrideMuiStyle={radioOverrideMuiStyle}
              />
              <Typography
                className="time"
                component="span"
                style={{ ...timeOfDayStyle }}
                sx={{ ...textStyle, ...textHoverStyle }}
              >
                No
              </Typography>
            </label>
          </Grid>
        </Grid>
      </div>
    )
  }

  useEffect(() => {
    getDates(moment().toString())
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [])

  return (
    <FormWizard.Page validate={validate}>
      <Container maxWidth="md">
        <Box sx={{ mt: 7, display: 'flex' }} flexDirection="column">
          <Box
            margin="auto"
            sx={{
              '@media (min-width:768px)': {
                width: '50%',
              },
              '@media (max-width:767px)': {
                width: '80%',
              },
            }}
          >
            <Typography
              fontSize={30}
              fontWeight={400}
              color="#595959"
              style={{ marginBottom: 20 }}
            >
              Book your Appointment
            </Typography>
            <Typography
              variant="body1"
              color="#595959"
              style={{ marginBottom: 20 }}
            >
              An adviser will call you back at the time you choose below
            </Typography>
            <FormError name="appointment" />
            {renderDateList()}
            {renderTimeOfDay()}
            {renderTime()}
            <Typography
              fontSize={18}
              color="#595959"
              fontWeight="bold"
              style={{ marginBottom: 20 }}
            >
              How would you prefer to be called?
            </Typography>
            {renderTelephoneCall()}
            {renderVideoCall()}
            {custom?.apiKey !== "2d6fe9fa-ead6-4bb4-b285-90967190067a" ? renderMeeting() : null}
            <Typography
              fontSize={18}
              color="#595959"
              fontWeight="bold"
              style={{ marginBottom: 20 }}
            >
              Contact Information?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12} lg={6}>
                <FormField
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  type="text"
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                <FormField
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  type="text"
                />
              </Grid>
              <Grid item xs={12}>
                <FormField
                  id="email"
                  name="email"
                  placeholder="Email"
                  type="email"
                />
              </Grid>
            </Grid>
            <FormButton
              id="book"
              type="submit"
              sx={{ fontWeight: 'bold', mt: 4, mb: 4 }}
              disableRipple
              fullWidth
              disabled={!form.getState()?.valid}
            >
              Book Now
            </FormButton>
          </Box>
        </Box>
      </Container>
    </FormWizard.Page>
  )
}

export default BookAppointment
